/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { LuisSidebar } from 'src/core/components/LuisSidebar';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useIcons } from 'src/core/hooks/useIcons';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LuisSidebarItemProps } from 'src/core/types/luisSidebar';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

const messages = defineMessages({
    tag: { id: 'customText.customTextSidebar.tag', defaultMessage: 'Data labeling' },
    preview: { id: 'customText.customTextSidebar.preview', defaultMessage: '(Preview)' },
    train: { id: 'customText.customTextSidebar.train', defaultMessage: 'Training jobs' },
    home: { id: 'customText.customTextSidebar.home', defaultMessage: 'Language Studio' },
    projects: { id: 'customText.customTextSidebar.projects', defaultMessage: 'Projects' },
    storage: { id: 'customText.customTextSidebar.storage', defaultMessage: 'My storage' },
    autoTag: { id: 'customText.customTextSidebar.autoTag', defaultMessage: 'Auto-labeling' },
    improve: { id: 'customText.customTextSidebar.improve', defaultMessage: 'Improve model' },
    deploy: { id: 'customText.customTextSidebar.deploy', defaultMessage: 'Deploying a model' },
    testModel: { id: 'customText.customTextSidebar.testModel', defaultMessage: 'Testing deployments' },
    modelDetails: { id: 'customText.customTextSidebar.modelDetails', defaultMessage: 'Model performance' },
    tenantRequests: { id: 'customText.customTextSidebar.tenantRequests', defaultMessage: 'Tenant Requests' },
    projectSettings: { id: 'customText.customTextSidebar.projectSettings', defaultMessage: 'Project settings' }
});

export const CustomTextSidebar = observer(() => {
    useIcons(['MachineLearning']);
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore, uiStore } = useGlobalState();

    const { currentCustomTextProjectStore } = rootStore;
    const currentModelType = currentCustomTextProjectStore?.projectKind;
    const currentProjectName = currentCustomTextProjectStore?.name;

    const autoTagRoutesMatchCondition = doesRouteMatchLocation(
        routeTable.customText.autoTaggingPage(currentProjectName, currentModelType),
        {
            locationToMatch: pathname
        }
    );

    const isExtractionProjects = doesRouteMatchLocation(routeTable.customText.extractionProjectsPage(), {
        locationToMatch: pathname
    });
    const isClassificationProjects = doesRouteMatchLocation(routeTable.customText.classificationProjectsPage(), {
        locationToMatch: pathname
    });

    const isTagRoutesMatchCondition = doesRouteMatchLocation(routeTable.customText.dataTaggingPage(currentProjectName, currentModelType), {
        locationToMatch: pathname
    });

    const moreDetailsItemIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextExamineModel'));

    React.useEffect(() => {
        if (
            uiStore.teachingBubbles.CustomTextTrainModelPage &&
            !uiStore.teachingBubbles.CustomTextExamineModel &&
            !uiStore.skippedTeachingBubbles.CustomTextTrainModelPage &&
            currentProjectName
        ) {
            createTeachingBubble(
                'CustomTextExamineModel',
                localize(customTextTeachingBubbleMessages.examineModelBody),
                localize(customTextTeachingBubbleMessages.examineModelTitle),
                `#${moreDetailsItemIdRef.current}`,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles), currentProjectName]);

    const items: LuisSidebarItemProps[] = [
        {
            iconName: 'Home',
            text: localize(messages.home),
            onClick: () => history.push(routeTable.languageStudio.root()),
            isSelected: doesRouteMatchLocation(routeTable.languageStudio.root(), { exact: true, locationToMatch: pathname })
        },
        ...(isExtractionProjects
            ? [
                  {
                      forceNoWrap: true,
                      iconName: 'AlignLeft',
                      text: localize(messages.projects),
                      onClick: () => history.push(routeTable.customText.extractionProjectsPage()),
                      isSelected: doesRouteMatchLocation(routeTable.customText.extractionProjectsPage(), {
                          exact: true,
                          locationToMatch: pathname
                      })
                  }
              ]
            : []),
        ...(isClassificationProjects
            ? [
                  {
                      forceNoWrap: true,
                      iconName: 'AlignLeft',
                      text: localize(messages.projects),
                      onClick: () => history.push(routeTable.customText.classificationProjectsPage()),
                      isSelected: doesRouteMatchLocation(routeTable.customText.classificationProjectsPage(), {
                          exact: true,
                          locationToMatch: pathname
                      })
                  }
              ]
            : []),
        { itemType: 'Header', text: currentProjectName },
        {
            iconName: 'Tag',
            text: localize(messages.tag),
            onClick: () => history.push(routeTable.customText.dataTaggingPage(currentProjectName, currentModelType)),
            isSelected: isTagRoutesMatchCondition,
            displayChildren: isTagRoutesMatchCondition || autoTagRoutesMatchCondition,
            items:
                isExtractionProjects || FeatureToggles.CustomTextGptLabeling
                    ? [
                          {
                              iconName: 'AutoEnhanceOn',
                              text: localize(messages.autoTag),
                              isSelected: autoTagRoutesMatchCondition,
                              secondaryText: localize(messages.preview),
                              onClick: () => history.push(routeTable.customText.autoTaggingPage(currentProjectName, currentModelType))
                          }
                      ]
                    : []
        },
        {
            iconName: 'MachineLearning',
            text: localize(messages.train),
            onClick: () => history.push(routeTable.customText.trainingPage(currentProjectName, currentModelType)),
            isSelected: doesRouteMatchLocation(routeTable.customText.trainingPage(currentProjectName, currentModelType), {
                locationToMatch: pathname
            })
        },
        {
            iconName: 'Product',
            id: moreDetailsItemIdRef.current,
            text: localize(messages.modelDetails),
            secondaryText: localize(messages.preview),
            onClick: () => history.push(routeTable.customText.modelDetailsListPage(currentProjectName, currentModelType)),
            isSelected: doesRouteMatchLocation(routeTable.customText.modelDetailsListPage(currentProjectName, currentModelType), {
                locationToMatch: pathname
            })
        },
        {
            iconName: 'Deploy',
            text: localize(messages.deploy),
            onClick: () => history.push(routeTable.customText.deployModelPage(currentProjectName, currentModelType)),
            isSelected: doesRouteMatchLocation(routeTable.customText.deployModelPage(currentProjectName, currentModelType), {
                locationToMatch: pathname
            })
        },
        {
            iconName: 'TestBeaker',
            text: localize(messages.testModel),
            isSelected: doesRouteMatchLocation(routeTable.customText.testModelsPage(currentProjectName, currentModelType), {
                locationToMatch: pathname
            }),
            onClick: () => history.push(routeTable.customText.testModelsPage(currentProjectName, currentModelType))
        },
        {
            iconName: 'Settings',
            text: localize(messages.projectSettings),
            onClick: () => history.push(routeTable.customText.projectSettingsPage(currentProjectName, currentModelType)),
            isSelected: doesRouteMatchLocation(routeTable.customText.projectSettingsPage(currentProjectName, currentModelType), {
                locationToMatch: pathname
            })
        }
    ];

    return currentCustomTextProjectStore ? <LuisSidebar itemLists={[items]} /> : null;
});
